import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { TeamsLinkPage } from './pages/TeamsLinkPage'
import { AuthGate } from './components/auth/AuthGate'
import { InstallPrompt } from './components/InstallPrompt'
const GlobalSearch = lazy(() =>
  import('./components/GlobalSearch').then(({ GlobalSearch }) => ({ default: GlobalSearch }))
)
const AppLayout = lazy(() =>
  import('./components/layout/AppLayout').then(({ AppLayout }) => ({ default: AppLayout }))
)
const LoginPage = lazy(() =>
  import('./pages/LoginPage').then(({ LoginPage }) => ({ default: LoginPage }))
)
const SignupPage = lazy(() =>
  import('./pages/SignupPage').then(({ SignupPage }) => ({ default: SignupPage }))
)
const SignupFormPage = lazy(() =>
  import('./pages/SignupFormPage').then(({ SignupFormPage }) => ({ default: SignupFormPage }))
)
const VerifyEmailPage = lazy(() =>
  import('./pages/VerifyEmailPage').then(({ VerifyEmailPage }) => ({ default: VerifyEmailPage }))
)
const FindWorkspacePage = lazy(() =>
  import('./pages/FindWorkspacePage').then(({ FindWorkspacePage }) => ({
    default: FindWorkspacePage,
  }))
)
const MFARequiredPage = lazy(() =>
  import('./pages/MFARequiredPage').then(({ MFARequiredPage }) => ({ default: MFARequiredPage }))
)
const TrialExpiredPage = lazy(() =>
  import('./pages/TrialExpiredPage').then(({ TrialExpiredPage }) => ({ default: TrialExpiredPage }))
)
const BillingPayPage = lazy(() =>
  import('./pages/BillingPayPage').then(({ BillingPayPage }) => ({ default: BillingPayPage }))
)
const AcceptInvitePage = lazy(() =>
  import('./pages/AcceptInvitePage').then(({ AcceptInvitePage }) => ({ default: AcceptInvitePage }))
)
const HomePage = lazy(() =>
  import('./pages/HomePage').then(({ HomePage }) => ({ default: HomePage }))
)
const IncidentsListPage = lazy(() =>
  import('./pages/IncidentsListPage').then(({ IncidentsListPage }) => ({
    default: IncidentsListPage,
  }))
)
const IncidentDetailPage = lazy(() =>
  import('./pages/IncidentDetailPage').then(({ IncidentDetailPage }) => ({
    default: IncidentDetailPage,
  }))
)
const OnCallPage = lazy(() =>
  import('./pages/OnCallPage').then(({ OnCallPage }) => ({ default: OnCallPage }))
)
const ScheduleDetailPage = lazy(() =>
  import('./pages/ScheduleDetailPage').then(({ ScheduleDetailPage }) => ({
    default: ScheduleDetailPage,
  }))
)
const EscalationPolicyDetailPage = lazy(() =>
  import('./pages/EscalationPolicyDetailPage').then(({ EscalationPolicyDetailPage }) => ({
    default: EscalationPolicyDetailPage,
  }))
)
const AlertDetailPage = lazy(() =>
  import('./pages/AlertDetailPage').then(({ AlertDetailPage }) => ({ default: AlertDetailPage }))
)
const PostMortemTemplatesPage = lazy(() =>
  import('./pages/PostMortemTemplatesPage').then(({ PostMortemTemplatesPage }) => ({
    default: PostMortemTemplatesPage,
  }))
)
const SettingsUsersPage = lazy(() =>
  import('./pages/SettingsUsersPage').then(({ SettingsUsersPage }) => ({
    default: SettingsUsersPage,
  }))
)
const SettingsAPIKeysPage = lazy(() =>
  import('./pages/SettingsAPIKeysPage').then(({ SettingsAPIKeysPage }) => ({
    default: SettingsAPIKeysPage,
  }))
)
const SettingsTeamsPage = lazy(() =>
  import('./pages/SettingsTeamsPage').then(({ SettingsTeamsPage }) => ({
    default: SettingsTeamsPage,
  }))
)
const SettingsUsagePage = lazy(() =>
  import('./pages/SettingsUsagePage').then(({ SettingsUsagePage }) => ({
    default: SettingsUsagePage,
  }))
)
const SystemSettingsPage = lazy(() =>
  import('./pages/SystemSettingsPage').then(({ SystemSettingsPage }) => ({
    default: SystemSettingsPage,
  }))
)
const SettingsMigrationsPage = lazy(() =>
  import('./pages/SettingsMigrationsPage').then(({ SettingsMigrationsPage }) => ({
    default: SettingsMigrationsPage,
  }))
)
const SettingsOrganizationPage = lazy(() =>
  import('./pages/SettingsOrganizationPage').then(({ SettingsOrganizationPage }) => ({
    default: SettingsOrganizationPage,
  }))
)
const SettingsProfilePage = lazy(() =>
  import('./pages/SettingsProfilePage').then(({ SettingsProfilePage }) => ({
    default: SettingsProfilePage,
  }))
)
const SettingsNotificationsPage = lazy(() =>
  import('./pages/SettingsNotificationsPage').then(({ SettingsNotificationsPage }) => ({
    default: SettingsNotificationsPage,
  }))
)
const VerifyContactMethodPage = lazy(() =>
  import('./pages/VerifyContactMethodPage').then(({ VerifyContactMethodPage }) => ({
    default: VerifyContactMethodPage,
  }))
)
const BillingPage = lazy(() =>
  import('./pages/BillingPage').then(({ BillingPage }) => ({
    default: BillingPage,
  }))
)
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then(({ AnalyticsPage }) => ({ default: AnalyticsPage }))
)
const IntegrationsPage = lazy(() =>
  import('./pages/IntegrationsPage').then(({ IntegrationsPage }) => ({ default: IntegrationsPage }))
)
const LogoutPage = lazy(() =>
  import('./pages/LogoutPage').then(({ LogoutPage }) => ({ default: LogoutPage }))
)
const SetupWizardPage = lazy(() =>
  import('./pages/SetupWizardPage').then(({ SetupWizardPage }) => ({ default: SetupWizardPage }))
)
const StatusPage = lazy(() =>
  import('./pages/StatusPage').then(({ StatusPage }) => ({ default: StatusPage }))
)
const PageAckPage = lazy(() =>
  import('./pages/PageAckPage').then(({ PageAckPage }) => ({ default: PageAckPage }))
)

/** Redirect /escalation-policies/:id → /on-call/escalation-paths/:id */
function EscalationPoliciesRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/on-call/escalation-paths/${id ?? ''}`} replace />
}

function App() {
  const [showSearch, setShowSearch] = useState(false)

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearch(true)
      }
    }
    const handleOpenSearch = () => setShowSearch(true)
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('open-search', handleOpenSearch)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      window.removeEventListener('open-search', handleOpenSearch)
    }
  }, [])

  // Register the service worker on first load (production only).
  useEffect(() => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
          if (import.meta.env.DEV) {
            console.log('[sw] registered', reg.scope)
          }
        })
        .catch((err) => {
          console.error('[sw] registration failed', err)
        })
    }
  }, [])

  return (
    <BrowserRouter>
      <AuthProvider>
        {showSearch && (
          <Suspense fallback={null}>
            <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
          </Suspense>
        )}
        {/* PWA install banner - only rendered on mobile when browser supports it */}
        <InstallPrompt />
        <AuthGate>
          <Suspense fallback={<RouteLoadingScreen />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Org-less self-serve signup (REG-177): the visitor has no
                account and no org yet, so this must not require either. */}
              <Route path="/signup" element={<SignupPage />} />
              {/* Password signup (REG-36), alongside the OAuth-only form
                above. /verify-email is the link that form's confirmation
                email points at; nothing is created until it is redeemed. */}
              <Route path="/signup/email" element={<SignupFormPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              {/* Org discovery (REG-178): the visitor has an account but
                cannot remember which subdomain it is on. */}
              <Route path="/find-workspace" element={<FindWorkspacePage />} />
              <Route path="/mfa-required" element={<MFARequiredPage />} />
              {/* The day-31 pay wall (REG-37). Reached by redirect from the
                API client on any 423, never linked to. */}
              <Route path="/trial-expired" element={<TrialExpiredPage />} />
              {/* Paddle's checkout link opens here (REG-46). Public: the
                transaction was fixed on the server, no session is needed. */}
              <Route path="/billing/pay" element={<BillingPayPage />} />
              {/* Shown after successful sign-out — always accessible, bypasses AuthGate */}
              <Route path="/logout" element={<LogoutPage />} />

              {/* Public status page — accessible without auth (OPE-111) */}
              <Route path="/status" element={<StatusPage />} />
              <Route path="/status/:token" element={<StatusPage />} />

              {/* SMS page link (REG-56). Public: the token is the credential. */}
              <Route path="/a/:token" element={<PageAckPage />} />

              {/* Email invitation accept flow — the invitee has no account yet (REG-29) */}
              <Route path="/invite/accept" element={<AcceptInvitePage />} />

              {/* Full-page onboarding wizard — requires auth, no sidebar */}
              <Route path="/setup" element={<SetupWizardPage />} />

              <Route element={<AppLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/incidents" element={<IncidentsListPage />} />
                <Route path="/teams/link" element={<TeamsLinkPage />} />
                <Route path="/incidents/:id" element={<IncidentDetailPage />} />

                {/* On-call unified section */}
                <Route path="/on-call" element={<OnCallPage />} />
                <Route path="/on-call/escalation-paths" element={<OnCallPage />} />
                <Route path="/on-call/schedules" element={<OnCallPage />} />
                {/* Detail pages (nested under on-call) */}
                <Route
                  path="/on-call/escalation-paths/:id"
                  element={<EscalationPolicyDetailPage />}
                />
                <Route path="/on-call/:id" element={<ScheduleDetailPage />} />

                {/* Backwards-compat redirects */}
                <Route path="/routing-rules" element={<Navigate to="/on-call" replace />} />
                <Route
                  path="/escalation-policies"
                  element={<Navigate to="/on-call/escalation-paths" replace />}
                />
                <Route path="/escalation-policies/:id" element={<EscalationPoliciesRedirect />} />

                <Route path="/alerts/:id" element={<AlertDetailPage />} />
                <Route path="/post-mortem-templates" element={<PostMortemTemplatesPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                {/* Slack and Discord installs redirect here with a result in the query string (REG-309). */}
                <Route path="/settings/integrations" element={<IntegrationsRedirect />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
                <Route path="/settings/profile" element={<SettingsProfilePage />} />
                <Route path="/settings/organization" element={<SettingsOrganizationPage />} />
                <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />
                <Route path="/verify-contact-method" element={<VerifyContactMethodPage />} />
                <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />
                <Route path="/settings/users" element={<SettingsUsersPage />} />
                <Route path="/settings/api-keys" element={<SettingsAPIKeysPage />} />
                <Route path="/settings/teams" element={<SettingsTeamsPage />} />
                <Route path="/settings/usage" element={<SettingsUsagePage />} />
                <Route path="/settings/system" element={<SystemSettingsPage />} />
                <Route path="/settings/migrations" element={<SettingsMigrationsPage />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthGate>
      </AuthProvider>
    </BrowserRouter>
  )
}

function IntegrationsRedirect() {
  const location = useLocation()
  return <Navigate to={{ pathname: '/integrations', search: location.search }} replace />
}

function RouteLoadingScreen() {
  return (
    <div className="min-h-[100dvh] bg-surface-canvas px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="h-8 w-44 animate-pulse rounded bg-surface-tertiary" />
        <div className="h-28 animate-pulse rounded-xl border border-border bg-surface-primary" />
        <div className="h-64 animate-pulse rounded-xl border border-border bg-surface-primary" />
      </div>
    </div>
  )
}

export default App
